import React from 'react';
import { PageTitle } from './Heading';

/**
 * Kanonischer Panel-Kopf (Phase 4 — Layout & Dichte).
 *
 * Kapselt das bisher 14× duplizierte Muster
 * `Icon w-6 h-6 + h2 text-2xl font-bold + p text-muted-foreground`.
 * Nutzt `PageTitle` aus `ui/Heading.jsx`, das bis dahin von keinem
 * Dashboard-Panel importiert wurde.
 *
 * Größen kommen aus der fluiden Skala (tailwind.config.js) — `text-2xl` ist auf
 * dem iPhone 20px und am Desktop 24px, ohne dass hier etwas zu tun ist.
 *
 * - icon:        lucide-Komponente (optional, nur noch aus Abwärtskompatibilität).
 *                Nicht für Titel verwenden: Seiten- und Panel-Titel stehen seit
 *                Oktober 2026 ohne Icon (Polishing iOS 1.0.3).
 * - title:       Überschrift
 * - description: erklärender Satz darunter (optional)
 * - actions:     rechts angedockte Bedienelemente (optional)
 * - display:     Überschrift in Bungee statt Poppins (Hauptseiten-Köpfe)
 * - children:    zusätzlicher Inhalt unter dem Kopf, z.B. eine Tab-Leiste
 */
const SectionHeader = ({
  icon: Icon,
  // Neutral (Oktober 2026, Heikos Entscheidung im Polishing für iOS 1.0.3).
  //
  // Von August bis Oktober stand hier Lila „als Anker“ für den Seitenkopf.
  // Inzwischen tragen Titel gar kein Icon mehr: Der Titel in Bungee ist der
  // Anker, und ein lila Symbol daneben verbrauchte ein Element des
  // Lila-Budgets (CorporateDesign: höchstens drei pro Bildschirm). Wer das
  // Icon aus Altbestand noch übergibt, bekommt es deshalb neutral.
  iconClassName = 'text-muted-foreground',
  title,
  meta,
  // `metaInline` haelt den Zaehler AUCH auf dem Telefon in der Titelzeile, mit
  // Mittelpunkt als Trenner ("Entdecken · 1 von 3"). Opt-in, weil es nur mit
  // einem kurzen Zaehler aufgeht — siehe die Begruendung am Wrapper unten.
  metaInline = false,
  description,
  actions,
  children,
  as = 'h2',
  compact = false,
  // Ueberschrift in der Display-Schrift (Bungee, Versalien) statt in Poppins.
  // Standard fuer die Hauptseiten-Koepfe, siehe ui/PageShell.jsx variant="main".
  // Nur fuer EIN bis ZWEI Woerter gedacht — Bungee laeuft breit, ein ganzer
  // Satz in Versalien sprengt auf dem Telefon die Zeile.
  display = false,
  className = '',
}) => (
  <div className={className}>
    {/* `compact` ist der Seitenkopf-Modus (PageShell variant="main"): eine Zeile,
        auch auf dem Telefon, und ein kleinerer Titel. Der Panel-Modus darunter
        bricht um, sobald Titel und Knoepfe nicht mehr nebeneinander passen —
        dort stehen rechts mehrere breite Knoepfe.

        `flex-wrap` statt `sm:flex-row`: Der Umbruch haengt am PLATZ, nicht am
        Fenster. `sm:` misst das Fenster, das Panel steckt aber manchmal in einem
        schmalen Kasten (Einrichtungsassistent: max-w-3xl). Am Schreibtisch war
        `sm:` dort aktiv, der Kopf blieb einzeilig, und "Neue Einladung" lag
        mitten auf dem Wort "Einladungsverwaltung". */}
    <div className={compact
      ? 'flex flex-row items-center justify-between gap-3'
      : 'flex flex-wrap items-start justify-between gap-3 sm:gap-4'}
    >
      <div className="min-w-0">
        {/* `meta` steht bewusst NEBEN bzw. UNTER der Ueberschrift statt darin: als
            Kind von PageTitle wuerde der Zaehler Teil des Accessible Name werden —
            die Ueberschrift hiesse dann "Lernsessions 0 aktiv · 0 geplant".
            Im Seitenkopf (compact) sitzt er auf dem Telefon in einer zweiten Zeile:
            Titel, Zaehler und Aktion nebeneinander drueckten sich dort auf 390px
            Breite gegenseitig zusammen. Ab sm: ist wieder Platz fuer eine Zeile.

            `metaInline` hebt das auf. Es gilt fuer kurze Zaehler, bei denen die
            zweite Zeile teurer ist als die paar Pixel Breite: In der Runden-Ansicht
            kostete sie rund 20px Kartenhoehe, und "Entdecken · 1 von 3" passt auf
            390px zusammen mit der Suchlupe locker in eine Zeile. Wer das Prop an
            einem laengeren Zaehler setzt, bekommt die Quetschung von damals zurueck. */}
        {/* Bei `metaInline` steht das Icon NEBEN der Ueberschrift statt darin.
            Das ist kein Geschmack, sondern noetig: PageTitle ist selbst ein
            Flex-Container, und die Schriftlinie eines Flex-Containers kommt vom
            ersten Element darin — also vom SVG, dessen Unterkante tiefer liegt
            als die Schriftlinie des Wortes. `items-baseline` richtete den Zaehler
            damit am Symbol aus statt am Titel: "· 3 von 3" hing 4px zu tief
            (nachgemessen). Ausserhalb der Ueberschrift bestimmt wieder der Text
            die Linie; das Icon bekommt `self-center` und sitzt optisch mittig.
            Fuer alle anderen Koepfe bleibt es beim bisherigen Aufbau. */}
        {/* `min-w-0` gehoert an JEDE Ebene dieser Flex-Kette, nicht nur an den
            Wrapper darueber: Ein Flex-Container hat von sich aus `min-width: auto`
            und schrumpft deshalb nie unter die Breite seines Inhalts. Ohne das
            hier lief eine lange Ueberschrift aus dem Elternteil heraus, und die
            `shrink-0`-Knoepfe rechts zeichneten sich (spaeter im DOM) darueber —
            im Einrichtungsassistenten (max-w-3xl) lag "Neue Einladung" mitten
            auf dem Wort "Einladungsverwaltung". */}
        <div className={compact && !metaInline
          ? 'flex min-w-0 flex-col sm:flex-row sm:flex-wrap sm:items-baseline gap-y-0.5 sm:gap-x-2'
          : `flex min-w-0 flex-wrap items-baseline ${metaInline ? 'gap-x-1.5' : 'gap-x-2'}`}
        >
          {metaInline && Icon && (
            <Icon className={`shrink-0 self-center mr-0.5 ${compact ? 'w-5 h-5' : 'w-5 h-5 sm:w-6 sm:h-6'} ${iconClassName}`} aria-hidden="true" />
          )}
          {/* Bungee braucht eine Stufe weniger Groesse als Poppins, um gleich
              gross zu WIRKEN: Versalien haben keine Unterlaengen, die Schrift
              laeuft breit, und "LERNSESSIONS" in text-lg schob auf 390px den
              Knopf daneben aus der Zeile. Die Klasse `.display-title` steht in
              index.css (Kapitel "Handschrift"). */}
          <PageTitle
            as={as}
            className={[
              metaInline ? 'min-w-0' : 'flex min-w-0 items-center gap-2',
              display ? 'display-title' : '',
              // eslint-disable-next-line no-nested-ternary
              display
                ? (compact ? 'text-base sm:text-lg' : 'text-xl sm:text-2xl')
                : (compact ? 'text-lg sm:text-2xl' : 'text-2xl'),
            ].filter(Boolean).join(' ')}
          >
            {!metaInline && Icon && (
              <Icon className={`shrink-0 ${compact ? 'w-5 h-5' : 'w-5 h-5 sm:w-6 sm:h-6'} ${iconClassName}`} aria-hidden="true" />
            )}
            <span className="min-w-0 break-words">{title}</span>
          </PageTitle>
          {meta != null && meta !== '' && (
            <span className="text-xs font-medium text-faint">
              {metaInline ? `· ${meta}` : meta}
            </span>
          )}
        </div>
        {description && (
          <p className="mt-1 text-sm sm:text-base text-muted-foreground break-words">{description}</p>
        )}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
    </div>
    {children}
  </div>
);

export default SectionHeader;
